import React, { useEffect, useState, useRef } from 'react';
import "./additionalSection.scss";
import XGSToggle from 'ui-components/xgs-toggle/xgs-toggle';
import { useDispatch, useSelector } from 'react-redux';
import { updateAdditionalSettingsSelector, updateAdditionalSettings } from 'slices/terminal-summary/additionalSettingsSlice';
import { useParams } from 'react-router-dom';
import { PathParams, TerminalSummaryPath } from '../route';
import Loading from 'ui-components/loading/loading';
import { Popover } from 'ui-components/popover/popover';
import { toast } from 'react-toastify';
import { UserUtils } from "app/data/user/userUtils";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import { getTerminalSummaryByTerminalNumber } from 'slices/terminal-summary/terminalDetailsSlice';
import { Divider } from '../common/divider';
import { Form, Formik, FormikProps } from 'formik';
import { getAdditionalSettingsValidation, TerminalInfoSettings } from 'app/data/terminal-details/models';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import ActionSection from '../common/actionSection';
import { UpdateAdditionalSettingsState } from 'slices/terminal-summary/AdditionalSettingsState';
import _ from 'lodash';

const routingAndLabels = [
    'Daily Pickups',
    'Allow Pickup Route',
    '4x6 Labels',
]

const controlSystems = [
    'RIC Control',
    'WMS Receipt',
    'DVIR Inspection',
    'VMU Usage',
]

const terminalClassification = [
    'Agent Terminal',
    'WG Locations',
    'Company Facility',
    'Bonded Facility',
    'Reporting Facility',
    'Physical Location'
];

const operationalFlags = [
    'Origin Facility',
    'Destination Facility'
]

const systemOptions = ['Uses Barcoding', 'Exclude from Web']

const tooltipText = {
    'Daily Pickups': 'Enable if terminal supports daily pickup service',
    'RIC Control': 'Enable remote inventory control features',
    'WMS Receipt': 'Integrate with Warehouse Management System Receipt',
    'DVIR Inspection': 'Driver Vehicle Inspection Report integration',
    'Allow Pickup Route': 'Enable to include terminal in pickup route planning',
    '4x6 Labels': 'Terminal uses 4x6 label format',
    'VMU Usage': 'Enable Vehicle Management Unit integration',
    'Agent Terminal': 'Mark as an agent-operated terminal',
    'WG Locations': 'Mark as a White Glove operated location',
    'Company Facility': "Identifies this terminal as a company-owned facility",
    'Bonded Facility': "Designates this terminal as bonded for customs clearance",
    'Reporting Facility': "Marks this terminal as a reporting location for compliance and logistics tracking",
    'Physical Location': "Mark this terminal as a physical location",
    'Origin Facility': "Enables this terminal to be marked as the origin point of a shipment",
    'Destination Facility': "Allows this terminal to be designated as a delivery destination",
    'Uses Barcoding': "Enables barcode scanning support for this terminal",
    'Exclude from Web': "Hides this terminal's data from web-based visibility or reporting tools"
};

const additionalSettingsMapping : Record<string, string> = {
    'Daily Pickups': 'dailyPickups',
    'RIC Control': 'ricControl',
    'WMS Receipt': 'wmsReceipt',
    'DVIR Inspection': 'dvirInspection',
    'Allow Pickup Route': 'allowPickupRoute',
    '4x6 Labels': 'fourBySixLabels',
    'VMU Usage': 'vmuUsage',
    'Agent Terminal': 'agentTerminal',
    'WG Locations': 'rlmTerminal',
    'Company Facility': 'companyFacility',
    'Bonded Facility': 'bondedFacility',
    'Reporting Facility': 'reportingFacility',
    'Physical Location': 'physicalLocation',
    'Origin Facility': 'originFlag',
    'Destination Facility': 'destinationFlag',
    'Uses Barcoding': 'barcodeFlag',
    'Exclude from Web': 'excludeFromWeb'
}
const AdditionalSettings = (): React.ReactElement => {
    const params = useParams() as PathParams;
    const terminalNumber = params[TerminalSummaryPath.number];
    const formRef = useRef<FormikProps<TerminalInfoSettings>>(null);
    const [spinner, setSpinner] = useState<boolean>(false);

    const userState: UserState = useSelector(userSelector);

    const dispatch = useDispatch();
    const updateAdditionalSettingsState: UpdateAdditionalSettingsState = useSelector(updateAdditionalSettingsSelector);
    const [settings, setSettings] = useState<Record<string, boolean>>({
        'Daily Pickups': updateAdditionalSettingsState.data?.dailyPickups || false,
        'RIC Control': updateAdditionalSettingsState.data?.ricControl || false,
        'WMS Receipt': updateAdditionalSettingsState.data?.wmsReceipt || false,
        'DVIR Inspection': updateAdditionalSettingsState.data?.dvirInspection || false,
        'Allow Pickup Route': updateAdditionalSettingsState.data?.allowPickupRoute || false,
        '4x6 Labels': updateAdditionalSettingsState.data?.fourBySixLabels || false,
        'VMU Usage': updateAdditionalSettingsState.data?.vmuUsage || false,
        'Agent Terminal': updateAdditionalSettingsState.data?.agentTerminal || false,
        'WG Locations': updateAdditionalSettingsState.data?.rlmTerminal || false,
        'Company Facility': updateAdditionalSettingsState.data?.companyFacility || false,
        'Bonded Facility': updateAdditionalSettingsState.data?.bondedFacility || false,
        'Reporting Facility': updateAdditionalSettingsState.data?.reportingFacility || false,
        'Physical Location': updateAdditionalSettingsState.data?.physicalLocation || false,
        'Origin Facility': updateAdditionalSettingsState.data?.originFlag || false,
        'Destination Facility': updateAdditionalSettingsState.data?.destinationFlag || false,
        'Uses Barcoding': updateAdditionalSettingsState.data?.barcodeFlag || false,
        'Exclude from Web': updateAdditionalSettingsState.data?.excludeFromWeb || false,
    });


    const toggleSetting =  (key: string) => {

        const updatedSettings = {
            ...settings,
            [key]: !settings[key],
        };

        setSettings(updatedSettings);

    };

    useEffect(() => {
        if (updateAdditionalSettingsState.isSuccess) {
            dispatch(getTerminalSummaryByTerminalNumber(terminalNumber));
        }
    }, [updateAdditionalSettingsState.isSuccess, dispatch, terminalNumber]);

    const handleInitialValues = () => {
        setSettings({
            'Daily Pickups': updateAdditionalSettingsState.data?.dailyPickups || false,
            'RIC Control': updateAdditionalSettingsState.data?.ricControl || false,
            'WMS Receipt': updateAdditionalSettingsState.data?.wmsReceipt || false,
            'DVIR Inspection': updateAdditionalSettingsState.data?.dvirInspection || false,
            'Allow Pickup Route': updateAdditionalSettingsState.data?.allowPickupRoute || false,
            '4x6 Labels': updateAdditionalSettingsState.data?.fourBySixLabels || false,
            'VMU Usage': updateAdditionalSettingsState.data?.vmuUsage || false,
            'Agent Terminal': updateAdditionalSettingsState.data?.agentTerminal || false,
            'WG Locations': updateAdditionalSettingsState.data?.rlmTerminal || false,
            'Company Facility': updateAdditionalSettingsState.data?.companyFacility || false,
            'Bonded Facility': updateAdditionalSettingsState.data?.bondedFacility || false,
            'Reporting Facility': updateAdditionalSettingsState.data?.reportingFacility || false,
            'Physical Location': updateAdditionalSettingsState.data?.physicalLocation || false,
            'Origin Facility': updateAdditionalSettingsState.data?.originFlag || false,
            'Destination Facility': updateAdditionalSettingsState.data?.destinationFlag || false,
            'Uses Barcoding': updateAdditionalSettingsState.data?.barcodeFlag || false,
            'Exclude from Web': updateAdditionalSettingsState.data?.excludeFromWeb || false,
        });
    }

    useEffect(() => {
        handleInitialValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateAdditionalSettingsState.data]);

    const handleSpinner = async () => {
        setTimeout(() => {
            setSpinner(false);
        }, 1000);
    };
    return (
        <>
            <Formik
                onSubmit={() => { }}
                initialValues={{
                    hub: updateAdditionalSettingsState.data?.hub?.toString() || '',
                    hubNumber: updateAdditionalSettingsState.data?.hubNumber?.toString() || '',
                    ratingZone: updateAdditionalSettingsState.data?.ratingZone?.toString() || '',
                    tcgTerminalNumber: updateAdditionalSettingsState.data?.tcgTerminalNumber?.toString() || '',
                    dailyPickups: updateAdditionalSettingsState.data?.dailyPickups || false,
                    ricControl: updateAdditionalSettingsState.data?.ricControl || false,
                    wmsReceipt: updateAdditionalSettingsState.data?.wmsReceipt || false,
                    dvirInspection: updateAdditionalSettingsState.data?.dvirInspection || false,
                    allowPickupRoute: updateAdditionalSettingsState.data?.allowPickupRoute || false,
                    fourBySixLabels: updateAdditionalSettingsState.data?.fourBySixLabels || false,
                    vmuUsage: updateAdditionalSettingsState.data?.vmuUsage || false,
                    agentTerminal: updateAdditionalSettingsState.data?.agentTerminal || false,
                    rlmTerminal: updateAdditionalSettingsState.data?.rlmTerminal || false,
                    companyFacility: updateAdditionalSettingsState.data?.companyFacility || false,
                    bondedFacility: updateAdditionalSettingsState.data?.bondedFacility || false,
                    reportingFacility: updateAdditionalSettingsState.data?.reportingFacility || false,
                    physicalLocation: updateAdditionalSettingsState.data?.physicalLocation || false,
                    originFlag: updateAdditionalSettingsState.data?.originFlag || false,
                    destinationFlag: updateAdditionalSettingsState.data?.destinationFlag || false,
                    barcodeFlag: updateAdditionalSettingsState.data?.barcodeFlag || false,
                    excludeFromWeb: updateAdditionalSettingsState.data?.excludeFromWeb || false,
                }}
                validationSchema={getAdditionalSettingsValidation()}
                innerRef={formRef}
                enableReinitialize
            >
                {(formikProps: FormikProps<TerminalInfoSettings>) => (
                    <Form>
                        <div className="additional-settings">
                            <div className="additional-settings__header">Operational Controls</div>
                            <div className="additional-settings__grid">
                                <div className='additional-settings__subTitle'>Routing and Labels</div>
                                {updateAdditionalSettingsState.data !== null ? <div className='additional-settings__options'>
                                    {routingAndLabels.map(setting => (
                                        <div key={setting} className="setting-item">
                                            {<XGSToggle
                                                onChange={() => {
                                                    formikProps.setFieldValue(additionalSettingsMapping[setting], !settings[setting]);
                                                    toggleSetting(setting)
                                                }}
                                                checked={settings[setting] as boolean}
                                                label={setting}
                                                disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                            />}
                                            <Popover values={<div>{tooltipText[setting as keyof typeof tooltipText]}</div>} />
                                        </div>
                                    ))}
                                </div> : <div className='additional-settings__loader'><Loading isLoading={true} /></div>}
                                <Divider className='margin-0' />
                                <div className='additional-settings__subTitle'>Control Systems</div>
                                {updateAdditionalSettingsState.data !== null ? <div className='additional-settings__options'>
                                    {controlSystems.map(setting => (
                                        <div key={setting} className="setting-item">
                                            <XGSToggle
                                                onChange={() => {
                                                    formikProps.setFieldValue(additionalSettingsMapping[setting], !settings[setting]);
                                                    toggleSetting(setting)
                                                }}
                                                checked={settings[setting] as boolean}
                                                label={setting}
                                                disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                            />
                                            <Popover values={<div>{tooltipText[setting as keyof typeof tooltipText]}</div>} />
                                        </div>
                                    ))}
                                </div> : <div className='additional-settings__loader'><Loading isLoading={true} /></div>}
                                <Divider className='margin-0' />
                                <div className='additional-settings__subTitle'>Terminal Classification</div>
                                {updateAdditionalSettingsState.data !== null ? <div className='additional-settings__options'>
                                    {terminalClassification.map(setting => (
                                        <div key={setting} className="setting-item">
                                            <XGSToggle
                                                onChange={() => {
                                                    formikProps.setFieldValue(additionalSettingsMapping[setting], !settings[setting]);
                                                    toggleSetting(setting)
                                                }}
                                                checked={settings[setting] as boolean}
                                                label={setting}
                                                disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                            />
                                            <Popover values={<div>{tooltipText[setting as keyof typeof tooltipText]}</div>} />
                                        </div>
                                    ))}
                                </div> : <div className='additional-settings__loader'><Loading isLoading={true} /></div>}
                                <Divider className='margin-0' />
                                <div className='additional-settings__subTitle'>Operational Flags</div>
                                {updateAdditionalSettingsState.data !== null ? <div className='additional-settings__options'>
                                    {operationalFlags.map(setting => (
                                        <div key={setting} className="setting-item">
                                            <XGSToggle
                                                onChange={() => {
                                                    formikProps.setFieldValue(additionalSettingsMapping[setting], !settings[setting]);
                                                    toggleSetting(setting)
                                                }}
                                                checked={settings[setting] as boolean}
                                                label={setting}
                                                disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                            />
                                            <Popover values={<div>{tooltipText[setting as keyof typeof tooltipText]}</div>} />
                                        </div>
                                    ))}
                                </div> : <div className='additional-settings__loader'><Loading isLoading={true} /></div>}
                                <Divider className='margin-0' />
                                <div className='additional-settings__subTitle'>System Options</div>
                                {updateAdditionalSettingsState.data !== null ? <div className='additional-settings__options'>  {systemOptions.map(setting => (
                                    <div key={setting} className="setting-item">
                                        <XGSToggle
                                            onChange={() => {
                                                formikProps.setFieldValue(additionalSettingsMapping[setting], !settings[setting]);
                                                toggleSetting(setting)
                                            }}
                                            checked={settings[setting] as boolean}
                                            label={setting}
                                            disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                        />
                                        <Popover values={<div>{tooltipText[setting as keyof typeof tooltipText]}</div>} />
                                    </div>
                                ))}</div> : <div className='additional-settings__loader'><Loading isLoading={true} /></div>}
                            </div>

                        </div>
                        <div className="additional-settings">
                            <div className="additional-settings__header">Terminal Information</div>
                            {updateAdditionalSettingsState.data === null ? <div className="additional-settings__loader"><Loading isLoading={true} /></div> :
                                <div className="additional-settings__grid">
                                    <div className="edit-flex-box">
                                        <div className="edit-flex-box__item">
                                            <XGSFormInput
                                                type="text"
                                                name="hub"
                                                required={false}
                                                labelMode={LabelModes.column}
                                                label="Regional Hub"
                                                className="width-30"
                                                disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                            />
                                            <XGSFormInput
                                                type="text"
                                                name="hubNumber"
                                                required={false}
                                                labelMode={LabelModes.column}
                                                label="Hub Number"
                                                className="width-30"
                                                disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                            />
                                            <XGSFormInput
                                                type="text"
                                                name="ratingZone"
                                                required={false}
                                                labelMode={LabelModes.column}
                                                label="Rating Zone"
                                                className="width-30"
                                                disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                            />
                                        </div>
                                        <div className="edit-flex-box__item margin-top-20">
                                            <XGSFormInput
                                                type="text"
                                                name="tcgTerminalNumber"
                                                required={false}
                                                labelMode={LabelModes.column}
                                                label="TCG Terminal Conversion Number"
                                                className="width-30"
                                                disabled={!UserUtils.isXGSAdministrator(userState.profile)}
                                            />
                                        </div>

                                    </div>
                                </div>}
                        </div>
                        {UserUtils.isXGSAdministrator(userState.profile) && (
                            <div className="action-section">
                                <ActionSection
                                    primaryButtonText="Save Terminal Settings"
                                    secondaryButtonText="Reset Changes"
                                    handleClear={() => {
                                        handleInitialValues();
                                        formRef.current?.resetForm();
                                    }}
                                    disabled={!formikProps.dirty}
                                    handleSave={async () => {
                                        setSpinner(true);
                                        await handleSpinner();
                                        await Promise.all([
                                            formRef.current?.submitForm()
                                        ]);
                                        if (_.isEmpty(formRef.current?.errors)) {
                                            dispatch(updateAdditionalSettings(terminalNumber, {
                                                // operational settings
                                                dailyPickups: settings['Daily Pickups'] as boolean,
                                                ricControl: settings['RIC Control'] as boolean,
                                                wmsReceipt: settings['WMS Receipt'] as boolean,
                                                dvirInspection: settings['DVIR Inspection'] as boolean,
                                                allowPickupRoute: settings['Allow Pickup Route'] as boolean,
                                                fourBySixLabels: settings['4x6 Labels'] as boolean,
                                                vmuUsage: settings['VMU Usage'] as boolean,
                                                agentTerminal: settings['Agent Terminal'] as boolean,
                                                rlmTerminal: settings['WG Locations'] as boolean,
                                                companyFacility: settings['Company Facility'] as boolean,
                                                bondedFacility: settings['Bonded Facility'] as boolean,
                                                reportingFacility: settings['Reporting Facility'] as boolean,
                                                physicalLocation: settings['Physical Location'] as boolean,
                                                originFlag: settings['Origin Facility'] as boolean,
                                                destinationFlag: settings['Destination Facility'] as boolean,
                                                barcodeFlag: settings['Uses Barcoding'] as boolean,
                                                excludeFromWeb: settings['Exclude from Web'] as boolean,
                                                // terminal information
                                                hub: formRef.current?.values.hub || '',
                                                hubNumber: formRef.current?.values.hubNumber || '',
                                                ratingZone: formRef.current?.values.ratingZone || '',
                                                tcgTerminalNumber: formRef.current?.values.tcgTerminalNumber || '',
                                            }, () => {
                                                toast.info("Updated details");
                                            }, (message) => {
                                                toast.error(message);
                                            }));
                                        }
                                    }}
                                    spinner={updateAdditionalSettingsState.isLoading || spinner}
                                    className="additional-settings__buttons"
                                />
                            </div>
                        )}
                    </Form>)}
            </Formik>

        </>
    );
};

export default AdditionalSettings;
